// components/home/useClubLifeTiles.js
//
// Builds the Home "Club Life" photo-feed tiles from the already-loaded
// `clubs` list. Moved verbatim out of HomeScreen (behaviour unchanged).

import * as React from "react";

export default function useClubLifeTiles(clubs) {
  // KAN-101 "Club Life" carousel — owner follow-up 2026-09-25: this is now a
  // PHOTO feed, not a club feed. Only clubs that actually have ≥1 gallery
  // photo are included at all (no more logo-only tiles for the rest); each
  // tile is ONE PHOTO, and a club with several photos contributes several
  // tiles, rotated round-robin with other clubs' photos rather than run
  // back to back. In prod today only 3 verified clubs have any gallery
  // photo — VibeLink Group (9 members), ColorStack UM (11), Canes for
  // Palestine (14) — every other club is skipped entirely (no logo
  // fallback tile), and if none qualify the whole section (incl. the "Club
  // Life" heading) renders nothing, same `length > 0` gate as before.
  // Built from the already-loaded `clubs` state (no new Firestore
  // query/index — getClubs is already verified+active only); recomputed
  // only when `clubs` changes.
  // Ordering: clubs sorted by member_count desc, then each club's OWN
  // photos are shuffled once and interleaved round-robin across clubs
  // (club1.photo1, club2.photo1, club3.photo1, club1.photo2, …), capped at
  // 40 tiles total.
  // The per-club shuffle is pinned in a ref (clubId -> {signature, order})
  // for the same reason the old single-photo-per-club pick used to be
  // pinned: `clubs` streams in page by page via
  // fetchRemainingClubsInBackground, so re-shuffling purely inside this
  // memo on every recompute would visibly reorder a club's photos each time
  // a later page arrived. A club's shuffle is only redone when the *set* of
  // its gallery URLs actually changed, checked via a sorted-join signature
  // (not on every memo recompute, and not just because the array reference
  // changed).
  const clubLifeShuffleRef = React.useRef(new Map()); // clubId -> { signature, order: string[] }
  const CLUB_LIFE_MAX_TILES = 40;
  const clubLifeClubs = React.useMemo(() => {
    const candidates = (clubs || [])
      .filter((c) => c && c.id && c.is_verified)
      .map((c) => ({
        club: c,
        // Deduped (Codex-caught): the gallery write path only filters for
        // non-empty strings, so the same URL can legitimately appear twice
        // in one club's gallery — which would produce two tiles with the
        // same `${clubId}:${url}` FlatList key.
        photos: Array.isArray(c.gallery)
          ? [...new Set(c.gallery.filter((u) => typeof u === "string" && u.length > 0))]
          : [],
      }))
      .filter((entry) => entry.photos.length > 0);

    candidates.sort(
      (a, b) => (b.club.member_count || 0) - (a.club.member_count || 0)
    );

    const perClubOrder = candidates.map(({ club, photos }) => {
      const signature = [...photos].sort().join("|");
      const cached = clubLifeShuffleRef.current.get(club.id);
      let order;
      if (cached && cached.signature === signature) {
        order = cached.order;
      } else {
        // Codex-caught: when a club's gallery CHANGES mid-session (a photo
        // added or removed while Home refreshes), do not reshuffle the
        // whole club — that moved every existing tile under the user's
        // finger. Keep the photos that are still present in their pinned
        // order, drop the removed ones, and shuffle only the NEW urls onto
        // the end. A club seen for the first time this session gets a
        // full Fisher-Yates shuffle.
        const photoSet = new Set(photos);
        const kept = cached ? cached.order.filter((u) => photoSet.has(u)) : [];
        const keptSet = new Set(kept);
        const fresh = photos.filter((u) => !keptSet.has(u));
        for (let i = fresh.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [fresh[i], fresh[j]] = [fresh[j], fresh[i]];
        }
        order = [...kept, ...fresh];
        clubLifeShuffleRef.current.set(club.id, { signature, order });
      }
      return { club, order };
    });

    const tiles = [];
    let round = 0;
    let addedAny = true;
    while (addedAny && tiles.length < CLUB_LIFE_MAX_TILES) {
      addedAny = false;
      for (const { club, order } of perClubOrder) {
        if (round >= order.length) continue;
        const url = order[round];
        tiles.push({
          key: `${club.id}:${url}`,
          clubId: club.id,
          club,
          url,
          logo: club.logo_url || club.logo || null,
          name: club.name,
        });
        addedAny = true;
        if (tiles.length >= CLUB_LIFE_MAX_TILES) break;
      }
      round += 1;
    }

    return tiles;
  }, [clubs]);
  return clubLifeClubs;
}
